import { create } from 'zustand';
import {
  CustomVariable,
  CreateCustomVariablePayload,
  UpdateCustomVariablePayload,
} from '@/types/api';
import {
  getCustomVariables as apiGetCustomVariables,
  createCustomVariable as apiCreateCustomVariable,
  updateCustomVariable as apiUpdateCustomVariable,
  deleteCustomVariable as apiDeleteCustomVariable,
  reorderCustomVariables as apiReorderCustomVariables,
} from '@/lib/api';
import { toast } from 'sonner';
import { useUIStore } from './uiStore';

interface CustomVariableState {
  variables: CustomVariable[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  isModalOpen: boolean;
  isEditMode: boolean;
  editingVariable: CustomVariable | null;
  openModal: () => void;
  closeModal: () => void;
  enterEditMode: () => void;
  exitEditMode: () => void;
  setEditingVariable: (variable: CustomVariable | null) => void;
  fetchVariables: () => Promise<void>;
  createVariable: (
    payload: CreateCustomVariablePayload,
  ) => Promise<CustomVariable | null>;
  updateVariable: (
    id: string,
    payload: UpdateCustomVariablePayload,
  ) => Promise<CustomVariable | null>;
  deleteVariable: (id: string) => Promise<void>;
  toggleVariable: (
    id: string,
    payload: UpdateCustomVariablePayload,
  ) => Promise<void>;
  reorderVariables: (orderedIds: string[]) => Promise<void>;
}

export const useCustomVariableStore = create<CustomVariableState>((set, get) => ({
  variables: [],
  isLoading: false,
  isInitialized: false,
  error: null,
  isModalOpen: false,
  isEditMode: false,
  editingVariable: null,

  openModal: () => set({ isModalOpen: true }),
  closeModal: () => {
    set({ isModalOpen: false, isEditMode: false, editingVariable: null });
    // Use a timeout to ensure focus happens after the dialog's closing animation.
    setTimeout(() => {
      useUIStore.getState().focusPromptInput?.();
    }, 0);
  },
  enterEditMode: () => set({ isEditMode: true }),
  exitEditMode: () => set({ isEditMode: false }),
  setEditingVariable: (variable) => set({ editingVariable: variable }),

  fetchVariables: async () => {
    // Skip if already initialized or currently loading (prevents duplicate fetch in Strict Mode)
    const { isInitialized, isLoading } = get();
    if (isInitialized || isLoading) return;
    
    set({ isLoading: true, error: null });
    try {
      const variables = await apiGetCustomVariables();
      set({ variables, isLoading: false, isInitialized: true });
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to fetch custom variables.';
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
    }
  },

  createVariable: async (payload) => {
    set({ isLoading: true });
    try {
      const newVariable = await apiCreateCustomVariable(payload);
      set((state) => ({
        variables: [...state.variables, newVariable].sort((a, b) =>
          a.key.localeCompare(b.key),
        ),
        isLoading: false,
      }));
      toast.success('Variable created successfully.');
      return newVariable;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to create variable.';
      toast.error(errorMsg);
      set({ isLoading: false });
      return null;
    }
  },

  updateVariable: async (id, payload) => {
    set({ isLoading: true });
    try {
      const updatedVariable = await apiUpdateCustomVariable(id, payload);
      set((state) => ({
        variables: state.variables
          .map((v) => (v.id === id ? updatedVariable : v))
          .sort((a, b) => a.key.localeCompare(b.key)), // Keep generic sort or respect reorder? For now, we might want to respect order_number if available.
        isLoading: false,
      }));
      toast.success('Variable updated successfully.');
      return updatedVariable;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to update variable.';
      toast.error(errorMsg);
      set({ isLoading: false });
      return null;
    }
  },

  deleteVariable: async (id: string) => {
    const originalVariables = get().variables;
    set((state) => ({
      variables: state.variables.filter((v) => v.id !== id),
    }));
    try {
      await apiDeleteCustomVariable(id);
      toast.success('Variable deleted successfully.');
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to delete variable.';
      toast.error(errorMsg);
      set({ variables: originalVariables });
    }
  },

  toggleVariable: async (id, payload) => {
    const originalVariables = get().variables;
    // Optimistic update
    set((state) => ({
      variables: state.variables.map((v) =>
        v.id === id ? { ...v, ...payload } : v,
      ),
    }));

    try {
      const updatedVariable = await apiUpdateCustomVariable(id, payload);
      // Replace the optimistically updated variable with the confirmed one from the server
      set((state) => ({
        variables: state.variables
          .map((v) => (v.id === id ? updatedVariable : v)),
          // We don't re-sort here to avoid jumping UI if name changes, 
          // but generally we trust the order_number maintained by reorderVariables
      }));
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to toggle custom variable.';
      toast.error(errorMsg);
      // Revert on failure
      set({ variables: originalVariables });
    }
  },

  reorderVariables: async (orderedIds) => {
    const originalVariables = get().variables;
    
    // Optimistic update
    const idMap = new Map(originalVariables.map(v => [v.id, v]));
    const reorderedVariables = orderedIds
      .map((id, index) => {
        const v = idMap.get(id);
        if (v) return { ...v, order_number: index };
        return null;
      })
      .filter((v): v is CustomVariable => v !== null);

    // Append any variables not in orderedIds (just in case)
    const remainingVariables = originalVariables.filter(v => !orderedIds.includes(v.id));
    
    set({ variables: [...reorderedVariables, ...remainingVariables] });

    try {
      await apiReorderCustomVariables(orderedIds);
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : 'Failed to reorder variables.';
      toast.error(errorMsg);
      // Revert
      set({ variables: originalVariables });
    }
  },
}));